import React from "react";
import { MEASURE_UNITS } from "../constants/AppConstants";
import { USER_DATA } from "../redux/constants/Auth";

class Utils {
  /**
   * Get first character from first & last sentences of a username
   * @param {String} name - Username
   * @return {String} 2 characters string
   */
  static getNameInitial(name) {
    let initials = name.match(/\b\w/g) || [];
    return ((initials.shift() || "") + (initials.pop() || "")).toUpperCase();
  }

  /**
   * Get current path related object from Navigation Tree
   * @param {Array} navTree - Navigation Tree from directory 'configs/NavigationConfig'
   * @param {String} path - Location path you looking for e.g '/app/dashboards/analytic'
   * @return {Object} object that contained the path string
   */
  static getRouteInfo(navTree, path) {
    if (navTree.path === path) {
      return navTree;
    }
    let route;
    for (let p in navTree) {
      if (navTree.hasOwnProperty(p) && typeof navTree[p] === "object") {
        route = this.getRouteInfo(navTree[p], path);
        if (route) {
          return route;
        }
      }
    }
    return route;
  }

  /**
   * Get accessible color contrast
   * @param {String} hex - Hex color code e.g '#3e82f7'
   * @return {String} 'dark' or 'light'
   */
  static getColorContrast(hex) {
    const threshold = 130;
    const hRed = hexToR(hex);
    const hGreen = hexToG(hex);
    const hBlue = hexToB(hex);

    function hexToR(h) {
      return parseInt(cutHex(h).substring(0, 2), 16);
    }

    function hexToG(h) {
      return parseInt(cutHex(h).substring(2, 4), 16);
    }

    function hexToB(h) {
      return parseInt(cutHex(h).substring(4, 6), 16);
    }

    function cutHex(h) {
      return h.charAt(0) === "#" ? h.substring(1, 7) : h;
    }

    const cBrightness = (hRed * 299 + hGreen * 587 + hBlue * 114) / 1000;
    if (cBrightness > threshold) {
      return "dark";
    } else {
      return "light";
    }
  }

  /**
   * Darken or lighten a hex color
   * @param {String} color - Hex color code e.g '#3e82f7'
   * @param {Number} percent - Percentage -100 to 100, positive for lighten, negative for darken
   * @return {String} Darken or lighten color
   */
  static shadeColor(color, percent) {
    let R = parseInt(color.substring(1, 3), 16);
    let G = parseInt(color.substring(3, 5), 16);
    let B = parseInt(color.substring(5, 7), 16);
    R = parseInt((R * (100 + percent)) / 100);
    G = parseInt((G * (100 + percent)) / 100);
    B = parseInt((B * (100 + percent)) / 100);
    R = R < 255 ? R : 255;
    G = G < 255 ? G : 255;
    B = B < 255 ? B : 255;
    const RR =
      R.toString(16).length === 1 ? `0${R.toString(16)}` : R.toString(16);
    const GG =
      G.toString(16).length === 1 ? `0${G.toString(16)}` : G.toString(16);
    const BB =
      B.toString(16).length === 1 ? `0${B.toString(16)}` : B.toString(16);
    return `#${RR}${GG}${BB}`;
  }

  static stringToHslColor(str, s, l) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }

    const h = hash % 360;
    return "hsl(" + h + ", " + s + "%, " + l + "%)";
  }

  /**
   * Returns either a positive or negative
   * @param {Number} number - number value
   * @param {any} positive - value that return when positive
   * @param {any} negative - value that return when negative
   * @return {any} positive or negative value based on param
   */
  static getSignNum(number, positive, negative) {
    if (number > 0) {
      return positive;
    }
    if (number < 0) {
      return negative;
    }
    return null;
  }

  /**
   * Returns either ascending or descending value
   * @param {Object} a - antd Table sorter param a
   * @param {Object} b - antd Table sorter param b
   * @param {String} key - object key for compare
   * @return {any} a value minus b value
   */
  static antdTableSorter(a, b, key) {
    if (typeof a[key] === "number" && typeof b[key] === "number") {
      return a[key] - b[key];
    }

    if (typeof a[key] === "string" && typeof b[key] === "string") {
      a = a[key].toLowerCase();
      b = b[key].toLowerCase();
      return a > b ? -1 : b > a ? 1 : 0;
    }
    return;
  }

  /**
   * Filter array of object
   * @param {Array} list - array of objects that need to filter
   * @param {String} key - object key target
   * @param {any} value  - value that excluded from filter
   * @return {Array} a value minus b value
   */
  static filterArray(list, key, value) {
    let data = list;
    if (list) {
      data = list.filter((item) => item[key] === value);
    }
    return data;
  }

  /**
   * Remove object from array by value
   * @param {Array} list - array of objects
   * @param {String} key - object key target
   * @param {any} value  - target value
   * @return {Array} Array that removed target object
   */
  static deleteArrayRow(list, key, value) {
    let data = list;
    if (list) {
      data = list.filter((item) => item[key] !== value);
    }
    return data;
  }

  /**
   * Wild card search on all property of the object
   * @param {Number | String} input - any value to search
   * @param {Array} list - array for search
   * @return {Array} array of object contained keyword
   */
  static wildCardSearch(list, input) {
    const searchText = (item) => {
      for (let key in item) {
        if (item[key] == null) {
          continue;
        }
        if (
          item[key]
            .toString()
            .toUpperCase()
            .indexOf(input.toString().toUpperCase()) !== -1
        ) {
          return true;
        }
      }
    };
    list = list.filter((value) => searchText(value));
    return list;
  }

  /**
   * Get Breakpoint
   * @param {Object} screens - Grid.useBreakpoint() from antd
   * @return {Array} array of breakpoint size
   */
  static getBreakPoint(screens) {
    let breakpoints = [];
    for (const key in screens) {
      if (screens.hasOwnProperty(key)) {
        const element = screens[key];
        if (element) {
          breakpoints.push(key);
        }
      }
    }
    return breakpoints;
  }

  static getInitials(name) {
    let initials = name.match(/\b\w/g) || [];
    return ((initials.shift() || "") + (initials.pop() || "")).toUpperCase();
  }

  static getShippingStatus = (status) => {
    if (
      status === "REPARTO" ||
      status === "PENDIENTE" ||
      status === "TRANSFERENCIA"
    ) {
      return "orange";
    }
    if (status === "INGRESADO" || status === "CHEQUE" || status === "PAGO") {
      return "cyan";
    }
    if (
      status === "ENTREGADO" ||
      status === "ACREDITADO" ||
      status === "CONTADO"
    ) {
      return "blue";
    }
    if (status === "CANCELADO" || status === "NO PAGO") {
      return "red";
    }
    if (status === "ANTICIPADO") {
      return "yellow";
    }
    return "";
  };

  static getDGICode = (status) => {
    if (status !== "00") {
      return "red";
    }
    if (status === "00") {
      return "green";
    }
    return "";
  };

  static getPaymentStatus = (status) => {
    if (status === "TRANSFERENCIA") {
      return "blue";
    }
    if (status === "CONTADO") {
      return "cyan";
    }
    if (status === "CREDITO") {
      return "orange";
    }
    return "";
  };

  static getParsedUnit = (unit) => {
    if (unit === MEASURE_UNITS[0]) {
      return "g";
    }
    if (unit === MEASURE_UNITS[1]) {
      return "ml";
    }
    if (unit === MEASURE_UNITS[2]) {
      return "un";
    }
    if (unit === "-") {
      return "s/u";
    }
    return "";
  };

  static getStockStatus = (stockCount) => {
    if (stockCount >= 10) {
      return (
        <div
          className="font-weight-bold"
          style={{
            backgroundColor: "rgb(82, 196, 26)",
            padding: "4px 8px",
            borderRadius: "0.675rem",
            color: "#FFFFFF",
          }}
        >
          <span>En Stock</span>
        </div>
      );
    }
    if (stockCount < 10 && stockCount > 0) {
      return (
        <div
          className="font-weight-bold"
          style={{
            backgroundColor: "#faad14",
            padding: "4px 8px",
            borderRadius: "0.675rem",
            color: "#FFFFFF",
          }}
        >
          <span>Limitado</span>
        </div>
      );
    }
    if (stockCount === 0) {
      return (
        <div
          className="font-weight-bold"
          style={{
            backgroundColor: "#ff6b72",
            padding: "4px 8px",
            borderRadius: "0.675rem",
            color: "#FFFFFF",
          }}
        >
          <span>Sin Stock</span>
        </div>
      );
    }
    return null;
  };

  static getStateStatus = (statePromotion) => {
    if (statePromotion === "Activada") {
      return (
        <div
          className="font-weight-bold"
          style={{
            backgroundColor: "rgb(82, 196, 26)",
            padding: "4px 8px",
            borderRadius: "0.675rem",
            color: "#FFFFFF",
          }}
        >
          <span>Activada</span>
        </div>
      );
    }
    if (statePromotion === "Desactivada") {
      return (
        <div
          className="font-weight-bold"
          style={{
            backgroundColor: "#ff6b72",
            padding: "4px 8px",
            borderRadius: "0.675rem",
            color: "#FFFFFF",
          }}
        >
          <span>Desactivada</span>
        </div>
      );
    }
    return null;
  };

  static weeklyGrowCalculator = (gross_weekly_sales, gross_last_week_sales) => {
    let growComparition =
      (gross_weekly_sales / gross_last_week_sales) * 100 - 100;
  };

  static getMeasureStockStatus = (measures) => {
    let measure = measures.measure;
    if (measure >= 10000) {
      return (
        <div
          className="font-weight-bold"
          style={{
            backgroundColor: "rgb(82, 196, 26)",
            padding: "4px 8px",
            borderRadius: "0.675rem",
            color: "#FFFFFF",
          }}
        >
          <span>En Stock</span>
        </div>
      );
    }
    if (measure < 10000 && measure > 0) {
      return (
        <div
          className="font-weight-bold"
          style={{
            backgroundColor: "#faad14",
            padding: "4px 8px",
            borderRadius: "0.675rem",
            color: "#FFFFFF",
          }}
        >
          <span>Limitado</span>
        </div>
      );
    }
    if (measure === 0) {
      return (
        <div
          className="font-weight-bold"
          style={{
            backgroundColor: "#ff6b72",
            padding: "4px 8px",
            borderRadius: "0.675rem",
            color: "#FFFFFF",
          }}
        >
          <span>Sin Stock</span>
        </div>
      );
    }
    return null;
  };

  static getLoggedUserRoles = () => {
    const loggedUser = JSON.parse(localStorage.getItem(USER_DATA))
      ? JSON.parse(localStorage.getItem(USER_DATA))
      : null;
    return loggedUser ? loggedUser.role : null;
  };

  static isChef = () => {
    return this.getLoggedUserRoles() === "chef";
  };

  static isAdmin = () => {
    return this.getLoggedUserRoles() === "administrator";
  };
}

export default Utils;
